/**
 * WizardStepUploadPdf — Passo dedicado a modelos do tipo "PDF pronto (upload)".
 * Permite ao usuário anexar o PDF específico deste contrato, gravado no bucket
 * `anexos` (mesmo bucket usado pelos demais anexos do CRM — reaproveita RLS
 * já validada). Esse arquivo será referenciado por `source_pdf_storage_path`
 * na geração do contrato, pulando as etapas de variáveis e preview.
 *
 * RELEASE: 2026-07 v1.1.0 - Bucket alterado para `anexos` (padrão do CRM).
 * RELEASE: 2026-05 v1.0.0 - Versão inicial.
 */
import React, { useRef } from 'react';
import { FileUp, FileText, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WizardStepUploadPdfProps {
  /** Tenant id (apenas para exibição/debug — o upload é feito pelo componente pai) */
  tenantId?: string;
  /** Caminho no storage do PDF já enviado nesta sessão (se houver) */
  uploadedPath: string | null;
  /** Nome original do arquivo (para exibição) */
  uploadedName: string | null;
  /** Se há upload em andamento */
  isUploading: boolean;
  /** Callback disparado ao selecionar um arquivo PDF */
  onUpload: (file: File) => void | Promise<void>;
  /** Remove o arquivo anexado (limpa estado local) */
  onClear: () => void;
}

export function WizardStepUploadPdf({
  uploadedPath,
  uploadedName,
  isUploading,
  onUpload,
  onClear,
}: WizardStepUploadPdfProps) {
  // Ref do input file — permite acionar via botão estilizado
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePick = () => inputRef.current?.click();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Limpa o valor do input para permitir re-selecionar o mesmo arquivo
    if (inputRef.current) inputRef.current.value = '';
    if (file) await onUpload(file);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">PDF do contrato</h3>
        <p className="text-sm text-muted-foreground">
          Envie o PDF finalizado deste contrato. O arquivo será usado como versão
          oficial do contrato — não passará por preenchimento de variáveis nem
          renderização automática.
        </p>
      </div>

      {/* Área de upload */}
      {!uploadedPath ? (
        <button
          type="button"
          onClick={handlePick}
          disabled={isUploading}
          className="w-full border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center gap-3 hover:bg-muted/40 transition-colors disabled:opacity-60"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
              <p className="text-sm text-muted-foreground">Enviando PDF...</p>
            </>
          ) : (
            <>
              <FileUp className="h-8 w-8 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium">Clique para selecionar o PDF</p>
                <p className="text-xs text-muted-foreground">Apenas arquivos .pdf</p>
              </div>
            </>
          )}
        </button>
      ) : (
        // Card com o arquivo já enviado
        <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
          <FileText className="h-8 w-8 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{uploadedName || 'contrato.pdf'}</p>
            <p className="text-xs text-muted-foreground">PDF enviado — pronto para gerar o contrato</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={isUploading}
            title="Remover PDF"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Input file oculto */}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
